import "./locationAddScreen.scss";
import {ITimelineItem} from "../../../shared/models/timeline.model";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {useNavigate, useSearchParams} from "react-router-dom";
import {CommonService} from "../../../shared/services";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {ImageConfig} from "../../../constants";
import LocationDetailsAddComponent from "./location-details-add/locationDetailsAddComponent";
import LocationPrimaryContactDetailsAddComponent
    from "./location-primary-contact-details-add/locationPrimaryContactDetailsAddComponent";
import LocationPhysicianAddComponent from "./location-physician-add/locationPhysicianAddComponent";
import LocationAdminDetailsAddComponent from "./location-admin-details-add/locationAdminDetailsAddComponent";
import LocationCriticalDetailsAddComponent from "./location-critical-details-add/locationCriticalDetailsAddComponent";
import LocationBillingDetailsAddComponent from "./location-billing-details-add/locationBillingDetailsAddComponent";
import LocationAccountPreferencesAddComponent
    from "./location-account-preferences-add/locationAccountPreferencesAddComponent";
import LocationBloodDrawInformationAddComponent
    from "./location-blood-draw-information-add/locationBloodDrawInformationAddComponent";
import Grid from "@mui/material/Grid";
import TimelineComponent from "../../../shared/components/timeline/timelineComponent";
import {useDispatch, useSelector} from "react-redux";
import {setCurrentNavParams, setSideMenuView} from "../../../store/actions/header.action";
import LottieFileGenarationComponent
    from "../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import LocationTestAndPanelsAddComponent from "./location-test-and-panels-add/LocationTestAndPanelsAddComponent";
import LocationUserDetailsAddComponent from "./location-user-details-add/LocationUserDetailsAddComponent";

interface LocationAddScreenProps {

}

const LOCATION_DETAILS = 'location_details';
const PRIMARY_CONTACT = 'primary_contact';
const TEST_AND_PANELS = 'test_and_panels';
const PROVIDER_OR_PHYSICIAN = 'Provider_or_Physician';
const CRITICAL_DETAILS = "critical_details";
const BILLING_DETAILS = "billing_details";
const ACCOUNT_PREFERENCES = "account_preferences";
const BLOOD_DRAW_INFORMATION = "blood_draw_information";
const ADMIN_DETAILS = "admin_details";
const USER_DETAILS = "user_details";

const STEPS: ITimelineItem[] = [
    {
        id: LOCATION_DETAILS,
        title: "Location Details",
    },
    {
        id: ADMIN_DETAILS,
        title: "Admin Details",
    },
    {
        id: USER_DETAILS,
        title: "User Details",
    },
    {
        id: PRIMARY_CONTACT,
        title: "Primary Contact",
    },
    {
        id: PROVIDER_OR_PHYSICIAN,
        title: "Provider/Physician",
    },
    {
        id: TEST_AND_PANELS,
        title: "Test and Panels",
    },
    {
        id: CRITICAL_DETAILS,
        title: "Critical Details",
    },
    {
        id: BILLING_DETAILS,
        title: "Billing Details",
    },
    {
        id: ACCOUNT_PREFERENCES,
        title: "Account Preferences",
    },
    {
        id: BLOOD_DRAW_INFORMATION,
        title: "Blood Draw Information",
    },

    // {
    //     id: EMAIL_VERIFICATION,
    //     title: "Email Verification",
    // }
];

const LocationAddScreen = (props: LocationAddScreenProps) => {

    const [steps] = useState<ITimelineItem[]>([..._.cloneDeep(STEPS)]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentStep, setCurrentStep] = useState<ITimelineItem | undefined>(STEPS[0]);
    const [activeStep, setActiveStep] = React.useState(0);
    const [locationId, setLocationId] = useState<number | undefined>(undefined);
    const [locationDetails, setLocationDetails] = useState<any>(undefined);
    const [isLocationDetailsLoading, setIsLocationDetailsLoading] = useState<boolean>(false);
    const facilityDetails = useSelector((state: any) => state.facilityAndLocationDetails.facilityDetails);
    const [selectedTestsAndPanels, setSelectedTestsAndPanels] = useState<any[]>([]);

    const navigate = useNavigate();

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setCurrentNavParams("", "Location"));
        dispatch(setSideMenuView('compact'));

    }, [dispatch]);

    const updateSteps = useCallback((currentStepIndex: number) => {
        setCurrentStep(STEPS[currentStepIndex]);
        searchParams.set("step", STEPS[currentStepIndex].id);
        setSearchParams(searchParams);
        setActiveStep(currentStepIndex);
    }, [searchParams, setSearchParams]);

    const getLocationDetails = useCallback(() => {
        const payload = {};
        let locationDetails: any = {};
        setIsLocationDetailsLoading(true);
        CommonService._location.getLocationDetails(locationId, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    locationDetails = response.data;
                }
                setLocationDetails(locationDetails);
                setIsLocationDetailsLoading(false);
            })
            .catch((error: any) => {
                // setLocationDetails(locationDetails);
                setIsLocationDetailsLoading(false);
            });
    }, [locationId]);

    const handleLocationCancel = useCallback(() => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Please confirm!',
            confirmationSubTitle: 'Are you sure you want to exit without adding this location?',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            yes: {
                text: "Exit",
                color: "primary"
            },
            no: {
                text: "Cancel",
                color: "primary"
            }
        }).then(() => {
            navigate(CommonService._routeConfig.LocationListRoute());
        }).catch(() => {
            // do nothing;
        });
    }, [navigate]);

    const handleADDSuccess = useCallback(() => {
        CommonService.openConfirmationDialog({
            confirmationTitle: 'Congratulations! ',
            confirmationSubTitle: 'Location has been successfully created.',
            disableEscKeyClose: false,
            disableBackdropClose: true,
            imagePath: `${ImageConfig.ConfirmationImage}`,
            direction: "up",
            hideNoOption: true,
            yes: {
                text: "Proceed",
                color: "primary"
            }

        }).then(() => {
            navigate(CommonService._routeConfig.LocationListRoute());
        })
            .catch(() => {
            });
    }, [navigate]);


    const handleNext = useCallback((currentStepIndex: number, currentStepId: string, locationId?: number, data?: any) => {
        if (parseInt(searchParams.get("lastCompletedStep") || "1") <= currentStepIndex) {
            searchParams.set("lastCompletedStep", currentStepIndex.toString());
            setSearchParams(searchParams);
        }
        switch (currentStepId) {
            case LOCATION_DETAILS :
                if (locationId) {
                    setLocationId(locationId);
                    searchParams.set("locationId", locationId.toString());
                    setSearchParams(searchParams);
                }
                if (data) {
                    setLocationDetails(data);
                }
                updateSteps(currentStepIndex);
                break;
            case ADMIN_DETAILS:
                if (data) {
                    setLocationDetails((oldSate: any) => {
                        return {
                            ...oldSate,
                            ...data
                        }
                    })
                }
                updateSteps(currentStepIndex);
                break;
            case USER_DETAILS:
                if (data) {
                    setLocationDetails((oldSate: any) => {
                        return {
                            ...oldSate,
                            userDetails: data
                        }
                    })
                }
                updateSteps(currentStepIndex);
                break;
            case PRIMARY_CONTACT:
                if (data) {
                    setLocationDetails((oldSate: any) => {
                        return {
                            ...oldSate,
                            ...data
                        }
                    })
                }
                updateSteps(currentStepIndex);
                break;
            case PROVIDER_OR_PHYSICIAN:
                updateSteps(currentStepIndex);
                break;
            case TEST_AND_PANELS:
                if (data) {
                    setSelectedTestsAndPanels(data);
                }
                updateSteps(currentStepIndex);
                break;
            case CRITICAL_DETAILS:
                if (data) {
                    setLocationDetails((oldSate: any) => {
                        return {
                            ...oldSate,
                            criticalDetails: data
                        }
                    })
                }
                updateSteps(currentStepIndex);
                break;
            case BILLING_DETAILS:
                if (data) {
                    setLocationDetails((oldSate: any) => {
                        return {
                            ...oldSate,
                            billingDetails: data
                        }
                    })
                }
                updateSteps(currentStepIndex);
                break;
            case ACCOUNT_PREFERENCES:
                if (data) {
                    setLocationDetails((oldSate: any) => {
                        return {
                            ...oldSate,
                            accountPreferences: data
                        }
                    })
                }
                updateSteps(currentStepIndex);
                break;
            case BLOOD_DRAW_INFORMATION:
                if (data) {
                    setLocationDetails((oldSate: any) => {
                        return {
                            ...oldSate,
                            bloodDrawInformation: data
                        }
                    });
                }
                handleADDSuccess();
                break;
            default:
                <></>
        }
    }, [searchParams, setSearchParams, updateSteps, handleADDSuccess]);

    useEffect(() => {
        const step = searchParams.get("step");
        setActiveStep(STEPS.findIndex((stepItem: any) => stepItem.id === step));
        const locationId = searchParams.get("locationId");
        const stepIDS = STEPS.map(item => item.id);
        let stepIndex = 0;
        if (locationId) {
            setLocationId(parseInt(locationId, 10));
            if (stepIDS.indexOf(step) > -1) {
                stepIndex = stepIDS.indexOf(step);
            } else {
                stepIndex = 1;
            }
        } else {
            stepIndex = 0;
            setActiveStep(0)
        }
        setCurrentStep(STEPS[stepIndex]);
    }, [searchParams]);


    const handlePrevious = useCallback((currentStepIndex: number, currentStepId: string) => {
        updateSteps(currentStepIndex);
    }, [updateSteps]);

    const renderComponent = useCallback(() => {
        if (!locationId) {
            if (currentStep?.id === LOCATION_DETAILS) {
                return <LocationDetailsAddComponent
                    facilityDetails={facilityDetails}
                    next={(locationId: number) => handleNext(1, LOCATION_DETAILS, locationId)}
                    cancel={handleLocationCancel}
                />
            }
        } else {
            switch (currentStep && currentStep.id) {
                case LOCATION_DETAILS:
                    return <LocationDetailsAddComponent
                        facilityDetails={facilityDetails}
                        locationId={locationId}
                        locationDetails={locationDetails}
                        next={(locationId: number) => handleNext(1, LOCATION_DETAILS, locationId)}
                        cancel={handleLocationCancel}
                    />
                case ADMIN_DETAILS:
                    return <LocationAdminDetailsAddComponent
                        locationId={locationId}
                        locationDetails={locationDetails}
                        cancel={handleLocationCancel}
                        next={(data: number) => handleNext(2, ADMIN_DETAILS, undefined, data)}
                        prev={() => handlePrevious(0, ADMIN_DETAILS)}
                    />
                case USER_DETAILS:
                    return <LocationUserDetailsAddComponent
                        locationId={locationId}
                        locationDetails={locationDetails}
                        cancel={handleLocationCancel}
                        next={(data: any) => handleNext(3, USER_DETAILS, undefined, data)}
                        prev={() => handlePrevious(1, USER_DETAILS)}
                    />
                case PRIMARY_CONTACT:
                    return <LocationPrimaryContactDetailsAddComponent
                        locationId={locationId}
                        cancel={handleLocationCancel}
                        locationDetails={locationDetails}
                        next={(data: number) => handleNext(4, PRIMARY_CONTACT, undefined, data)}
                        prev={() => handlePrevious(2, PRIMARY_CONTACT)}
                    />
                case PROVIDER_OR_PHYSICIAN:
                    return <LocationPhysicianAddComponent
                        locationId={locationId}
                        facilityId={locationDetails?.facilityId}
                        cancel={handleLocationCancel}
                        next={() => handleNext(5, PROVIDER_OR_PHYSICIAN, undefined)}
                        prev={() => handlePrevious(3, PROVIDER_OR_PHYSICIAN)}
                    />
                case TEST_AND_PANELS:
                    return <LocationTestAndPanelsAddComponent
                        locationId={locationId}
                        cancel={handleLocationCancel}
                        locationDetails={locationDetails}
                        next={(data: number) => handleNext(6, TEST_AND_PANELS, undefined, data)}
                        prev={() => handlePrevious(4, TEST_AND_PANELS)}
                        selectedTestsAndPanels={selectedTestsAndPanels}
                    />
                case CRITICAL_DETAILS:
                    return <LocationCriticalDetailsAddComponent
                        locationId={locationId}
                        locationDetails={locationDetails}
                        cancel={handleLocationCancel}
                        next={(data: number) => handleNext(7, CRITICAL_DETAILS, undefined, data)}
                        prev={() => handlePrevious(5, CRITICAL_DETAILS)}
                    />
                case BILLING_DETAILS:
                    return <LocationBillingDetailsAddComponent
                        locationId={locationId}
                        locationDetails={locationDetails}
                        cancel={handleLocationCancel}
                        next={(data: number) => handleNext(8, BILLING_DETAILS, undefined, data)}
                        prev={() => handlePrevious(6, BILLING_DETAILS)}
                    />
                case ACCOUNT_PREFERENCES:
                    return <LocationAccountPreferencesAddComponent
                        locationId={locationId}
                        locationDetails={locationDetails}
                        cancel={handleLocationCancel}
                        next={(data: number) => handleNext(9, ACCOUNT_PREFERENCES, undefined, data)}
                        prev={() => handlePrevious(7, ACCOUNT_PREFERENCES)}
                    />
                case BLOOD_DRAW_INFORMATION:
                    return <LocationBloodDrawInformationAddComponent
                        locationId={locationId}
                        locationDetails={locationDetails}
                        cancel={handleLocationCancel}
                        prev={() => handlePrevious(8, BLOOD_DRAW_INFORMATION)}
                        next={(data: number) => handleNext(10, BLOOD_DRAW_INFORMATION, undefined, data)}
                    />
                // case EMAIL_VERIFICATION:
                //     return <LocationEmailVerificationAddComponent
                //         locationId={locationId}
                //         locationDetails={locationDetails}
                //         cancel={handleLocationCancel}
                //         next={(data: number) => handleNext(10, EMAIL_VERIFICATION, undefined, data)}
                //         prev={() => handlePrevious(8, EMAIL_VERIFICATION)}
                //     />
                default:
                    <></>
            }
        }
    }, [locationId, facilityDetails, currentStep, handleNext, handleLocationCancel, handlePrevious, locationDetails, selectedTestsAndPanels]);

    useEffect(() => {
        if (locationId) {
            getLocationDetails();
        }
    }, [locationId, getLocationDetails]);

    const handleStepClick = useCallback((step: any) => {
        const lastCompletedStep = parseInt(searchParams.get("lastCompletedStep") || '0');
        const clickedStepIndex = STEPS.findIndex((stepItem: any) => stepItem.id === step.id);
        if (clickedStepIndex <= (lastCompletedStep)) {
            setActiveStep(clickedStepIndex);
            setCurrentStep(step);
            searchParams.set("step", step.id);
            setSearchParams(searchParams);
        }
    }, [searchParams, setSearchParams]);

    return (
        <div className="location-creation-layout-screen add-layout-with-timeline screen ">
            <Grid container spacing={2} className="add-form-timeline-wrapper">
                <Grid item sm={12} md={3} xl={3} className="timeline-layout"
                      sx={{
                          display: {xs: "none", md: "block"}
                      }}>
                    <div className="add-layout-title">Adding Location</div>
                    <div className="add-layout-timeline">
                        <TimelineComponent
                            activeStep={activeStep}
                            steps={steps}
                            handleStepClick={handleStepClick}
                        />
                    </div>
                </Grid>
                <Grid item xs={12} sm={12} md={9} xl={9} className="add-form-content">
                    <div className="add-layout-form-content">
                        {renderComponent()}
                    </div>
                </Grid>
            </Grid>
            {isLocationDetailsLoading &&
                <div className={"glass-effect h-v-center flex-direction-column"}>
                    <LottieFileGenarationComponent
                        loop={true}
                        autoplay={true}
                        animationData={ImageConfig.LoadingLottieFile}/>
                </div>
            }
        </div>
    );

};

export default LocationAddScreen;
